import type {
  ConvertLeadResponse,
  CreateClientRequest,
  LeadDTO,
  ListLeadsQuery,
  Paginated,
  UpdateLeadStatusRequest,
} from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { toast } from '../../components/ui/Toast';
import { leadsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function useLeadsList(query: ListLeadsQuery): UseQueryResult<Paginated<LeadDTO>> {
  return useQuery({
    queryKey: queryKeys.leads.list(query as Record<string, unknown>),
    queryFn: ({ signal }) => leadsApi.list(query, signal),
    placeholderData: (prev) => prev,
  });
}

/** Parameterized by the mutation call (not the hook call) — a leads table
 * has many rows, each needing to trigger this against its own id. */
export function useSetLeadStatus(): UseMutationResult<
  LeadDTO,
  unknown,
  { id: string; status: UpdateLeadStatusRequest['status'] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => leadsApi.setStatus(id, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.leads.all });
      toast.success('Lead status updated');
    },
  });
}

/** Converts a lead into a real Client. Invalidates both leads (the row
 * flips to CONVERTED) and clients (the new client should appear in the
 * register) lists. Parameterized the same way as `useSetLeadStatus`. */
export function useConvertLead(): UseMutationResult<
  ConvertLeadResponse,
  unknown,
  { id: string; body: CreateClientRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => leadsApi.convert(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.leads.all });
      void qc.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast.success('Lead converted to client');
    },
  });
}
